/**
 * League section card: header (flag + league name + live count + chevron) + list of compact match rows.
 * Tapping the header opens league detail when leagueId is present on match data.
 */

import React, { useCallback, useMemo } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { ChevronRight } from 'lucide-react-native';
import { useRouter } from 'expo-router';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useTheme } from '../../../src/theme/useTheme';
import { CountryFlag } from '../../worldcup/CountryFlag/CountryFlag';
import { ScoreboardMatchRow } from '../ScoreboardMatchRow/ScoreboardMatchRow';
import type { GameResult } from '../../../src/gameResults/types/gameResults.types';
import { GAME_STATUSES } from '../../../src/gameResults/constants/gameResults.constants';
import { buildLeagueDetailHref } from '../../../src/gameResults/helpers/buildLeagueDetailHref.helper';
import { getLeagueSectionStyles } from './LeagueSection.styles';

export interface LeagueSectionProps {
  leagueName: string;
  games: GameResult[];
  testID?: string;
}

export const LeagueSection: React.FC<LeagueSectionProps> = ({ leagueName, games, testID }) => {
  const theme = useTheme();
  const styles = useMemo(() => getLeagueSectionStyles(theme), [theme]);
  const router = useRouter();
  const leagueCountryCode = games[0]?.leagueCountryCode;
  const leagueId = games[0]?.leagueId;

  const liveCount = games.filter((g) => g.status === GAME_STATUSES.LIVE).length;

  const handleHeaderPress = useCallback(() => {
    if (leagueId === undefined || leagueId === '') return;
    const code = games[0]?.leagueCountryCode;
    router.push(buildLeagueDetailHref(leagueName, leagueId, code));
  }, [games, leagueId, leagueName, router]);

  const openMatchHub = useCallback(
    (gameId: string) => {
      router.push({
        pathname: '/app/match/[gameId]',
        params: { gameId, returnTo: '/app/gameResults' },
      });
    },
    [router],
  );

  const isNavigable = leagueId != null && leagueId !== '';

  const headerInner = (
    <>
      {leagueCountryCode != null && leagueCountryCode.length > 0 && (
        <View style={styles.headerFlagWrap}>
          <CountryFlag
            countryCode={leagueCountryCode}
            size="compact"
            accessibilityLabel={`${leagueName} country flag`}
          />
        </View>
      )}
      <Text style={styles.headerText} numberOfLines={1}>
        {leagueName}
      </Text>
      {liveCount > 0 && (
        <View style={styles.liveCountBadge}>
          <View style={styles.liveCountDot} />
          <Text style={styles.liveCountText}>{liveCount} LIVE</Text>
        </View>
      )}
      {isNavigable && (
        <ChevronRight size={16} color={theme.gray[400]} />
      )}
    </>
  );

  return (
    <View style={styles.card} testID={testID ?? `league-section-${leagueName}`}>
      {isNavigable ? (
        <Pressable
          style={({ pressed, hovered }) => [
            styles.header,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.headerPressed,
            hovered && Platform.OS === 'web' && styles.headerHover,
          ]}
          onPress={handleHeaderPress}
          accessibilityRole="button"
          accessibilityLabel={`Open ${leagueName} league`}
        >
          {headerInner}
        </Pressable>
      ) : (
        <View style={styles.header} accessibilityRole="header">
          {headerInner}
        </View>
      )}
      <View style={styles.matchesList}>
        {games.map((game, index) => (
          <ScoreboardMatchRow
            key={game.id}
            game={game}
            isLast={index === games.length - 1}
            testID={testID ? `${testID}-row-${game.id}` : undefined}
            onPress={(g) => {
              openMatchHub(g.id);
            }}
          />
        ))}
      </View>
    </View>
  );
};
